import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, unref as _unref, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "report-editor" }
const _hoisted_2 = { class: "pages-container" }
const _hoisted_3 = { class: "page-controls" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "page-number" }
const _hoisted_6 = ["onClick", "disabled"]
const _hoisted_7 = ["onClick", "disabled"]
const _hoisted_8 = { class: "a4-page" }
const _hoisted_9 = {
  key: 0,
  class: "page-header"
}
const _hoisted_10 = { class: "header-content" }
const _hoisted_11 = ["onBlur"]
const _hoisted_12 = ["onBlur"]
const _hoisted_13 = { class: "page-content" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "block-controls" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = ["onClick", "disabled"]
const _hoisted_18 = ["onClick", "disabled"]
const _hoisted_19 = {
  key: 1,
  class: "page-footer"
}
const _hoisted_20 = ["onBlur"]
const _hoisted_21 = {
  key: 0,
  class: "edit-panel"
}
const _hoisted_22 = { class: "edit-panel-header" }
const _hoisted_23 = { class: "edit-panel-content" }

import { ref, reactive, computed, onMounted } from 'vue'
import draggable from 'vuedraggable'

// Components
import TextBlock from './components/blocks/TextBlock.vue'
import DataBlock from './components/blocks/DataBlock.vue'
import TableBlock from './components/blocks/TableBlock.vue'
import ImageBlock from './components/blocks/ImageBlock.vue'
import TextEdit from './components/editors/TextEdit.vue'
import DataEdit from './components/editors/DataEdit.vue'
import TableEdit from './components/editors/TableEdit.vue'
import ImageEdit from './components/editors/ImageEdit.vue'

// Interfaces
interface Block {
  id: string
  type: 'text' | 'data' | 'table' | 'image'
  data: any
  style: any
}

interface Page {
  id: string
  header?: {
    title: string
    subtitle: string
  }
  footer?: {
    content: string
  }
  blocks: Block[]
}

interface Report {
  title: string
  pages: Page[]
  formData: any
}

// Props - dados do formulário que já existem

export default /*@__PURE__*/_defineComponent({
  __name: '__index',
  props: {
    formData: {}
  },
  setup(__props: any) {

const props = __props

// Estado
const report = reactive<Report>({
  title: 'Relatório de Higiene Ocupacional',
  pages: [],
  formData: {}
})

const selectedBlock = ref<Block | null>(null)

// Inicialização com dados existentes
onMounted(() => {
  initializeReport()
})

function initializeReport() {
  report.formData = props.formData
  report.pages = [createNewPage()]
  
  // Adiciona blocos iniciais com os dados do formulário
  const firstPage = report.pages[0]
  
  // Bloco de título
  firstPage.blocks.push(createTextBlock('<h2>Relatório de Higiene Ocupacional</h2>'))
  
  // Bloco de dados gerais
  firstPage.blocks.push(createDataBlock('dados-gerais', 'Dados Gerais da Avaliação'))
  
  // Bloco de tabela com parâmetros
  firstPage.blocks.push(createTableBlock('parametros', 'Parâmetros Avaliados'))
  
  // Bloco de observações
  firstPage.blocks.push(createTextBlock('<h3>Observações e Recomendações</h3><p>Digite as observações aqui...</p>'))
}

function createNewPage(): Page {
  return {
    id: generateId(),
    header: {
      title: 'Relatório de Higiene Ocupacional',
      subtitle: 'Sistema de Gestão de Higiene Ocupacional'
    },
    footer: {
      content: `Página 1 | Emitido em ${new Date().toLocaleDateString('pt-BR')}`
    },
    blocks: []
  }
}

function createTextBlock(content: string): Block {
  return {
    id: generateId(),
    type: 'text',
    data: { content },
    style: { fontSize: '14px', lineHeight: '1.6' }
  }
}

function createDataBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: 'data',
    data: { 
      dataKey,
      title,
      fields: getDataFields(dataKey)
    },
    style: { backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px' }
  }
}

function createTableBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: 'table',
    data: {
      dataKey,
      title,
      columns: getTableColumns(dataKey),
      data: getTableData(dataKey)
    },
    style: { margin: '20px 0' }
  }
}

function createImageBlock(): Block {
  return {
    id: generateId(),
    type: 'image',
    data: { src: '', alt: 'Imagem do relatório', caption: '' },
    style: { textAlign: 'center', margin: '20px 0' }
  }
}

// Funções auxiliares para obter dados do formulário
function getDataFields(dataKey: string) {
  const fieldsMap: any = {
    'dados-gerais': [
      { label: 'Local', value: report.formData?.local || '' },
      { label: 'Data da Avaliação', value: report.formData?.assessmentDate || '' },
      { label: 'Avaliador', value: report.formData?.evaluator || '' },
      { label: 'Empresa', value: report.formData?.company || '' }
    ]
  }
  return fieldsMap[dataKey] || []
}

function getTableColumns(dataKey: string) {
  const columnsMap: any = {
    'parametros': [
      { key: 'parameter', label: 'Parâmetro', editable: true },
      { key: 'value', label: 'Valor Medido', editable: true },
      { key: 'limit', label: 'Limite', editable: true },
      { key: 'unit', label: 'Unidade', editable: true },
      { key: 'status', label: 'Status', editable: false }
    ]
  }
  return columnsMap[dataKey] || []
}

function getTableData(dataKey: string) {
  const dataMap: any = {
    'parametros': report.formData?.parameters?.map((param: any) => ({
      parameter: param.name,
      value: param.measuredValue,
      limit: param.limit,
      unit: param.unit,
      status: param.measuredValue <= param.limit ? 'Dentro' : 'Acima'
    })) || []
  }
  return dataMap[dataKey] || []
}

// Métodos do Editor
function addPage() {
  report.pages.push(createNewPage())
}

function removePage(pageIndex: number) {
  if (report.pages.length > 1) {
    report.pages.splice(pageIndex, 1)
  }
}

function movePageUp(pageIndex: number) {
  if (pageIndex > 0) {
    const page = report.pages.splice(pageIndex, 1)[0]
    report.pages.splice(pageIndex - 1, 0, page)
  }
}

function movePageDown(pageIndex: number) {
  if (pageIndex < report.pages.length - 1) {
    const page = report.pages.splice(pageIndex, 1)[0]
    report.pages.splice(pageIndex + 1, 0, page)
  }
}

function addTextBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createTextBlock('<p>Digite seu texto aqui...</p>'))
}

function addDataBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createDataBlock('custom-data', 'Dados Personalizados'))
}

function addTableBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createTableBlock('custom-table', 'Tabela Personalizada'))
}

function addImageBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createImageBlock())
}

function removeBlock(pageIndex: number, blockIndex: number) {
  report.pages[pageIndex].blocks.splice(blockIndex, 1)
  selectedBlock.value = null
}

function moveBlockUp(pageIndex: number, blockIndex: number) {
  if (blockIndex > 0) {
    const blocks = report.pages[pageIndex].blocks
    const block = blocks.splice(blockIndex, 1)[0]
    blocks.splice(blockIndex - 1, 0, block)
  }
}

function moveBlockDown(pageIndex: number, blockIndex: number) {
  const blocks = report.pages[pageIndex].blocks
  if (blockIndex < blocks.length - 1) {
    const block = blocks.splice(blockIndex, 1)[0]
    blocks.splice(blockIndex + 1, 0, block)
  }
}

function selectBlock(block: Block) {
  selectedBlock.value = block
}

function updateBlockData(pageIndex: number, blockIndex: number, newData: any) {
  report.pages[pageIndex].blocks[blockIndex].data = {
    ...report.pages[pageIndex].blocks[blockIndex].data,
    ...newData
  }
}

function onBlockEdit(updatedBlock: Block) {
  if (selectedBlock.value) {
    Object.assign(selectedBlock.value, updatedBlock)
  }
}

function onContentEdit(event: Event) {
  // Salva automaticamente as alterações de conteúdo
  saveReport()
}

function onBlockReorder() {
  saveReport()
}

function updateHeader(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement
  if (report.pages[pageIndex].header) {
    report.pages[pageIndex].header!.title = target.innerText
  }
}

function updateSubtitle(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement
  if (report.pages[pageIndex].header) {
    report.pages[pageIndex].header!.subtitle = target.innerText
  }
}

function updateFooter(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement
  if (report.pages[pageIndex].footer) {
    report.pages[pageIndex].footer!.content = target.innerText
  }
}

// Componentes dinâmicos
function getBlockComponent(type: string) {
  const components: any = {
    text: TextBlock,
    data: DataBlock,
    table: TableBlock,
    image: ImageBlock
  }
  return components[type]
}

function getEditComponent(type: string) {
  const components: any = {
    text: TextEdit,
    data: DataEdit,
    table: TableEdit,
    image: ImageEdit
  }
  return components[type]
}

// Utilitários
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

function saveReport() {
  // Salvar no localStorage ou API
  localStorage.setItem('occupational-hygiene-report', JSON.stringify(report))
  console.log('Relatório salvo:', report)
}

function exportPDF() {
  // Implementar exportação PDF
  alert('Exportando para PDF...')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", { class: "toolbar" }, [
      _createElementVNode("div", { class: "toolbar-group" }, [
        _createElementVNode("button", {
          onClick: addTextBlock,
          class: "toolbar-btn"
        }, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("span", null, "📝", -1),
          _createTextVNode(" Texto ", -1)
        ]))]),
        _createElementVNode("button", {
          onClick: addDataBlock,
          class: "toolbar-btn"
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("span", null, "📊", -1),
          _createTextVNode(" Dados ", -1)
        ]))]),
        _createElementVNode("button", {
          onClick: addTableBlock,
          class: "toolbar-btn"
        }, [...(_cache[3] || (_cache[3] = [
          _createElementVNode("span", null, "📋", -1),
          _createTextVNode(" Tabela ", -1)
        ]))]),
        _createElementVNode("button", {
          onClick: addImageBlock,
          class: "toolbar-btn"
        }, [...(_cache[4] || (_cache[4] = [
          _createElementVNode("span", null, "🖼️", -1),
          _createTextVNode(" Imagem ", -1)
        ]))])
      ]),
      _createElementVNode("div", { class: "toolbar-group" }, [
        _createElementVNode("button", {
          onClick: addPage,
          class: "toolbar-btn"
        }, [...(_cache[5] || (_cache[5] = [
          _createElementVNode("span", null, "📄", -1),
          _createTextVNode(" Nova Página ", -1)
        ]))]),
        _createElementVNode("button", {
          onClick: exportPDF,
          class: "toolbar-btn primary"
        }, [...(_cache[6] || (_cache[6] = [
          _createElementVNode("span", null, "📤", -1),
          _createTextVNode(" Exportar PDF ", -1)
        ]))]),
        _createElementVNode("button", {
          onClick: saveReport,
          class: "toolbar-btn secondary"
        }, [...(_cache[7] || (_cache[7] = [
          _createElementVNode("span", null, "💾", -1),
          _createTextVNode(" Salvar ", -1)
        ]))])
      ])
    ]),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(report.pages, (page, pageIndex) => {
        return (_openBlock(), _createElementBlock("div", {
          key: page.id,
          class: "page-wrapper"
        }, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("button", {
              onClick: ($event: any) => (removePage(pageIndex)),
              class: "page-btn danger"
            }, " 🗑️ ", 8, _hoisted_4),
            _createElementVNode("span", _hoisted_5, "Página " + _toDisplayString(pageIndex + 1), 1),
            _createElementVNode("button", {
              onClick: ($event: any) => (movePageUp(pageIndex)),
              class: "page-btn",
              disabled: pageIndex === 0
            }, " ⬆️ ", 8, _hoisted_6),
            _createElementVNode("button", {
              onClick: ($event: any) => (movePageDown(pageIndex)),
              class: "page-btn",
              disabled: pageIndex === report.pages.length - 1
            }, " ⬇️ ", 8, _hoisted_7)
          ]),
          _createElementVNode("div", _hoisted_8, [
            (page.header)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("h1", {
                      contenteditable: "true",
                      onBlur: ($event: any) => (updateHeader(pageIndex, $event)),
                      onInput: onContentEdit
                    }, _toDisplayString(page.header.title), 41, _hoisted_11),
                    _createElementVNode("div", {
                      contenteditable: "true",
                      class: "header-subtitle",
                      onBlur: ($event: any) => (updateSubtitle(pageIndex, $event)),
                      onInput: onContentEdit
                    }, _toDisplayString(page.header.subtitle), 41, _hoisted_12)
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_13, [
              _createVNode(_unref(draggable), {
                modelValue: page.blocks,
                "onUpdate:modelValue": ($event: any) => ((page.blocks) = $event),
                "item-key": "id",
                class: "blocks-container",
                onEnd: onBlockReorder,
                handle: ".drag-handle"
              }, {
                item: _withCtx(({ element: block, index: blockIndex }) => [
                  _createElementVNode("div", {
                    class: _normalizeClass(['block', `block-${block.type}`, { 'selected': selectedBlock.value?.id === block.id }]),
                    onClick: ($event: any) => (selectBlock(block))
                  }, [
                    _createElementVNode("div", _hoisted_15, [
                      _cache[8] || (_cache[8] = _createElementVNode("div", { class: "drag-handle" }, "⠿", -1)),
                      _createElementVNode("button", {
                        onClick: ($event: any) => (removeBlock(pageIndex, blockIndex)),
                        class: "block-btn danger"
                      }, " × ", 8, _hoisted_16),
                      _createElementVNode("button", {
                        onClick: ($event: any) => (moveBlockUp(pageIndex, blockIndex)),
                        class: "block-btn",
                        disabled: blockIndex === 0
                      }, " ↑ ", 8, _hoisted_17),
                      _createElementVNode("button", {
                        onClick: ($event: any) => (moveBlockDown(pageIndex, blockIndex)),
                        class: "block-btn",
                        disabled: blockIndex === page.blocks.length - 1
                      }, " ↓ ", 8, _hoisted_18)
                    ]),
                    (_openBlock(), _createBlock(_resolveDynamicComponent(getBlockComponent(block.type)), {
                      block: block,
                      pageIndex: pageIndex,
                      blockIndex: blockIndex,
                      onUpdate: ($event: any) => (updateBlockData(pageIndex, blockIndex, $event)),
                      onContentEdit: onContentEdit
                    }, null, 40, ["block", "pageIndex", "blockIndex", "onUpdate"]))
                  ], 10, _hoisted_14)
                ]),
                _: 2
              }, 1032, ["modelValue", "onUpdate:modelValue"])
            ]),
            (page.footer)
              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                  _createElementVNode("div", {
                    contenteditable: "true",
                    class: "footer-content",
                    onBlur: ($event: any) => (updateFooter(pageIndex, $event)),
                    onInput: onContentEdit
                  }, _toDisplayString(page.footer.content), 41, _hoisted_20)
                ]))
              : _createCommentVNode("", true)
          ])
        ]))
      }), 128))
    ]),
    (selectedBlock.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
          _createElementVNode("div", _hoisted_22, [
            _cache[9] || (_cache[9] = _createElementVNode("h3", null, "Editar Bloco", -1)),
            _createElementVNode("button", {
              onClick: _cache[0] || (_cache[0] = ($event: any) => (selectedBlock.value = null)),
              class: "close-btn"
            }, "×")
          ]),
          _createElementVNode("div", _hoisted_23, [
            (_openBlock(), _createBlock(_resolveDynamicComponent(getEditComponent(selectedBlock.value.type)), {
              block: selectedBlock.value,
              onUpdate: onBlockEdit
            }, null, 40, ["block"]))
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})